/**
 * Modal density / Bonello-criterion geometry — takes an already-enumerated
 * list of room-mode frequencies (this package does NOT compute room modes;
 * that's rectangular-room eigenmode physics owned by the caller, e.g.
 * `calculateRoomModes` in web/src/lib/acousticsPhysics.ts) and turns it into
 * a 1/3-octave mode-count histogram.
 *
 * Bonello's criterion (Bonello, 1981): a well-behaved room has a mode count
 * per 1/3-octave band that does not DECREASE as frequency rises — a dip
 * means a frequency region with too few modes to smooth the response, a
 * likely audible gap. This module flags exactly that (count below the
 * previous non-empty band's count) — it does not attempt to detect every
 * possible modal-overlap problem (e.g. near-coincident individual modes),
 * just the standard band-density check.
 *
 * The Schroeder frequency (caller-supplied — see calculateSchroederFrequency)
 * is drawn as a reference line: above it modes are statistically dense
 * enough that individual counting stops being meaningful.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, logFreqToX, type LinearScale, type LogFreqScale } from './scales';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import { computeOctaveBandCenters, type OctaveFraction } from './rta';

export interface ModalDensityBand {
  centerHz: number;
  loHz: number;
  hiHz: number;
  count: number;
  /** This band's count is lower than the last non-empty band below it —
   *  violates Bonello's non-decreasing-density guideline. */
  violatesBonello: boolean;
  x0: number;
  x1: number;
  y: number;
}

export interface ModalDensityInput {
  modeFreqsHz: number[];
  /** Reference line — draw null/omit to skip it. */
  schroederHz?: number | null;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  bandsPerOctave?: OctaveFraction;
  measureWidth?: (label: string) => number;
}

export interface ModalDensityGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  maxCount: number;
  bands: ModalDensityBand[];
  /** Plot-local x of the Schroeder frequency line; null if outside range or unset. */
  schroederX: number | null;
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeModalDensityGeometry(opts: ModalDensityInput): ModalDensityGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 300;
  const bandsPerOctave = opts.bandsPerOctave ?? 3;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  if (!opts.modeFreqsHz.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, 0, 1, true),
      maxCount: 0, bands: [], schroederX: null, xTicks, yTicks: [],
    };
  }

  const centers = computeOctaveBandCenters(fMin, fMax, bandsPerOctave);
  const edgeRatio = 2 ** (1 / (2 * bandsPerOctave));
  const counts = centers.map((c) => {
    const lo = c / edgeRatio;
    const hi = c * edgeRatio;
    return opts.modeFreqsHz.filter((f) => f >= lo && f < hi).length;
  });

  const maxCount = Math.max(1, ...counts);
  const yScale = createLinearScale(frame.plotH, 0, maxCount, true);

  let lastNonEmpty = 0;
  const bands: ModalDensityBand[] = centers.map((center, i) => {
    const lo = center / edgeRatio;
    const hi = center * edgeRatio;
    const count = counts[i]!;
    const violatesBonello = count > 0 && count < lastNonEmpty;
    if (count > 0) lastNonEmpty = count;
    return {
      centerHz: center, loHz: lo, hiHz: hi, count, violatesBonello,
      x0: logFreqToX(xScale, Math.max(lo, fMin)),
      x1: logFreqToX(xScale, Math.min(hi, fMax)),
      y: linearToPlot(yScale, count),
    };
  });

  const schroederHz = opts.schroederHz;
  const schroederX = schroederHz != null && schroederHz >= fMin && schroederHz <= fMax
    ? logFreqToX(xScale, schroederHz)
    : null;

  const yStep = Math.max(1, Math.ceil(maxCount / 5));
  const yTicks: YTick[] = linearTicks(0, maxCount, yStep).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: v === 0, label: `${Math.round(v)}`,
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, maxCount, bands, schroederX, xTicks, yTicks };
}
